/* 
 * React component to show a calendar for one or more people.
 * The fullCallendar component is used; see https://fullcalendar.io/docs#toc
 * 
 */

import React, { useState, useRef, useCallback, useEffect }
    from 'react'

import FullCalendar from '@fullcalendar/react'
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import resourceDayGridPlugin from '@fullcalendar/resource-daygrid';
import interactionPlugin from '@fullcalendar/interaction';

import TeamCalendarOverlay from '../Team/TeamCalendarOverlay'
import useBookingState, { bookingActive }
    from '../Booking/bookingReducer';
import { LoadingIndicator as Loading } from '../../lib/iprs-react-library/src/index.js';
import teamEventSource from '../Team/TeamCalendarEventSource';
import handleDrop from '../Team/TeamCalendarDropHandler';
import { getDateStr } from '../../lib/iprs-react-library/src/index.js';
import userLoggedInHasRole              from '../Util/userLoggedInHasRole';

/* This is the license key which allows use of paid for views of the calendar.
 * See env.local and .env files. */
const schedulerLicenseKey = process.env.REACT_APP_SCHEDULER_LICENSE_KEY

const AltCalendar = ({ api, state, eventDispatch }) => {

    const calendarApi = useRef()

    const { team: { teamID, teams = [], date = new Date() },
        appType: { appointmentTypes = [] } = {} } = state

    const teamName = (0 === teamID) ? 0 : teams.filter(t => t.pkTeamId === teamID)[0]?.TeamName;

    let slotInterval, slotLabelInterval, omitZeroMinute, slotMinTime;
    /* temporary fix based on teamID in UAT and production for VH which want to 
     * align with main appType which is 20mins */
    if (2025 === teamID || 3023 === teamID) {
        slotMinTime = '07:40:00';
        slotInterval = '00:20';
        slotLabelInterval = slotInterval;
        omitZeroMinute = false;
    } else {
        slotMinTime = '08:00:00';
        slotInterval = '00:30';
        slotLabelInterval = '01:00';
        omitZeroMinute = true;
    }

    const [bookingState] = useBookingState(state, eventDispatch)

    const [rerender, doRerender] = useState(1)

    const [loading, setLoading] = useState(false);

    /* 
     * This is the last event fired by the calendar, wrapped in an object with 
     * an action field so we can diaspay an overlay.
     */
    const [calendarEvent, setCalendarEvent] = useState(null)

    /* we either show availability in the background or appointments in background*/
    const [rotaMode, setRotaMode] = useState(false);
    const [qeMode, setQEMode] = useState(true);
    const [hideEmpty, setHideEmpty] = useState(false);

    const hasDiaryRotaPerm = userLoggedInHasRole(api, 'Diary Rota');

    const handleModeChange = e => {

        if ('rotaMode' === e.target.name) {
            setRotaMode(e.target.checked)
        } else if ('hideEmpty') {
            setHideEmpty(e.target.checked);
        } else {
            setQEMode(e.target.checked)
        }
        calendarApi.current.getApi().refetchEvents()
    }

    useEffect(() => {
        const refreshTime = Number.parseInt(
            process.env.REACT_APP_IPRS_WEB_CALENDAR_REFRESH_PERIOD_MS
        );
        if (refreshTime > 0) {
            const timedReload = setInterval(() => {
                const current = calendarApi.current;
                current?.getApi()?.refetchEvents();
            }, refreshTime);
            return () => clearInterval(timedReload);
        }
    }, [calendarApi]);

    // used to filter which people are displayed
    const [teamFilter, setTeamFilter] = useState([])

    const eventSource = useCallback((info, successCallback, failureCallback) => {

        teamEventSource({
            cal: { info, successCallback, failureCallback },
            other: {
                teamID, api, rotaMode, teamFilter, bookingState,
                appointmentTypes
            }
        });

    }, [teamID, api, rotaMode, teamFilter, appointmentTypes, bookingState]);

    const resourceSource = useCallback((info, successCallback, failureCallback) => {
        api.getTeamMemberList(teamID)
            .then(response => response.apiResult)
            .then(apiResult => apiResult.filter(
                // if there's a team filter then only include results matching the filter
                r => !teamFilter.length || teamFilter.includes(r.pkPartyID)
            ))
            .then(apiResult => {
                return apiResult.map(a => { return { id: a.pkPartyID, title: a.FirstName + ' ' + a.LastName } })
            })
            .then(resources => {
                successCallback(resources)
            })
            .catch(error => failureCallback(error))
    }, [teamID, teamFilter, api])


    /* Called by the calendar when the user selects a region of the calendar; 
     * this selects calendar time not a particular event. */
    const handleSelectRegion = eventInfo => {

        let action = null;
        if (!rotaMode && bookingActive(bookingState)) {

            // overlay will allow time to be fine edited
            action = 'BookingProcessOverlay';
        } else {

            action = rotaMode ? 'selectRotaRegion' : 'selectRegion'

            // action to say that we have selected empty space
        }
        setCalendarEvent({ action, event: eventInfo })
    }

    /* Handles a click on an event. Currently we just delete the event with no
     * confirmation step */
    const handleClick = eventInfo => {

        let action = rotaMode ? 'rotaEventClick' : 'eventClick'

        if (!rotaMode) {
            if (eventInfo.event.extendedProps.apiEvent.BookingID) {


                action = 'bookingEventClick'


            } else {
                action = 'NCAEventClick'; // Non Clinical Activity
            }
        } else {
            action = 'rotaEventClick'
        }

        // action to say that we have clicked something not selected empty space
        setCalendarEvent({ action, event: eventInfo.event })
    }

    /* Called by the calendar when the user resizes an event; i.e when they drag 
     * the start or end of the event */
    const handleResize = eventResizeInfo => {

        const oldEvent = eventResizeInfo.oldEvent
        const event = eventResizeInfo.event

        const oldAPIEvent = oldEvent.extendedProps.apiEvent;
        const {
            RotaID: rotaID, PartyID: partyID, RotaTypeID: rotaTypeID,
            Description: desc = '',
            Title: title = '',
            Available: available
        } = oldAPIEvent

        if (rotaMode) {
            api.updateRota(rotaID,
                event.start,
                event.end,
                null,
                partyID,
                rotaTypeID)
                .then(response => {

                    /* hack to get REACT to re-render*/
                    doRerender(rerender + 1)
                })
                .catch(e => {
                    console.log(e)
                })
        } else {
            api.updateTask(rotaID, event.start, event.end, desc, partyID, rotaTypeID,
                available, title)
                .then(response => {

                    /* hack to get REACT to re-render*/
                    doRerender(rerender + 1)
                })
                .catch(e => {
                    console.log(e)
                })
        }

    }

    /* Called by the calendar component to render an individual event */
    const eventContent = (ev) => {

        const evAPISrc = ev.event.extendedProps.hasOwnProperty('apiEvent') ?
            ev.event.extendedProps.apiEvent : {};

        const { RotaTypeID: rotaTypeID, BookingID: bookingID,
            CaseID: caseID = 0, Name: patientName = '', AppointmentTypes: evApTypes = [] } = evAPISrc

        const appTypeList = () => {
            return appointmentTypes.filter(at => evApTypes.includes(at.AppointmentTypeID))
                .map(at => <li key={at.AppointmentTypeID}>{at.Title}</li>);
        }

        const appTypes = () => {

            if (1 === rotaTypeID) {
                return (
                    <div style={{}} >

                        <ul className={'unstyled'} >{appTypeList()}</ul>
                    </div>)
            } else {
                return ''
            }
        }

        let title = null
        if (bookingID) {
            title = '' + caseID + (patientName ? ' (' + patientName + ')' : '')
        } else {
            title = ev.event.title
        }
        return (
            <div style={{ maxHeight: '100%', maxWidth: '100%', overflow: 'hidden' }} >
                <h1 style={{ fontSize: '1rem', color: 'white' }} >{title}</h1>
                {appointmentTypes ? appTypes() : null}
            </div>
        )
    }

    const dayHeaderContent = (data) => {
        return getDateStr(data.date);
    }

    const handleResourceClick = e => {
        e.preventDefault()

        // this will also be the partyID
        const resourceID = Number.parseInt(e.target.name, 10)

        setTeamFilter([resourceID])
    }

    const resourceLabelContent = info => {

        const { title: name, id } = info.resource

        return <a href='/' name={id} onClick={handleResourceClick} >{name}</a>
    }

    const floatStyle = {
        float: 'left',
        marginRight: '1em'
    };

    /* warp with callback to reduce re-renders */
    const eventDrop = useCallback(
        (info) => handleDrop(info, api, setCalendarEvent, rotaMode, calendarApi),
        [api, setCalendarEvent, rotaMode, calendarApi]);

    if (!teamID) {
        return ''
    } else {
        return (
            <>

                <TeamCalendarOverlay api={api} calendarEvent={calendarEvent}
                    setCalendarEvent={setCalendarEvent} calendarApi={calendarApi}
                    state={state} eventDispatch={eventDispatch} />


                <h1 className={'calHead'} >{'\'' + teamName + '\' Team Calendar'}<Loading loading={loading} /></h1>

                <FullCalendar
                    ref={calendarApi}
                    plugins={[resourceTimeGridPlugin, resourceDayGridPlugin, interactionPlugin]}
                    initialView='resourceTimeGridDay'
                    locale='en-gb'
                    views={{
                        resourceTimeGridDay: { titleFormat: { weekday: 'short', year: 'numeric', month: 'short', day: '2-digit' } }
                    }}
                    businessHours={{
                        // days of week. an array of zero-based day of week integers (0=Sunday)
                        daysOfWeek: [1, 2, 3, 4, 5, 6], // Monday - Saturday

                        startTime: '08:00', // a start time (10am in this example)
                        endTime: '18:00', // an end time (6pm in this example)
                    }}
                    initialDate={date}
                    selectable={true}
                    selectMirror={true}
                    hiddenDays={[0] /* hide */}
                    slotDuration={slotInterval}
                    slotLabelInterval={slotLabelInterval}
                    slotMinTime={slotMinTime}
                    slotMaxTime='20:00:00'
                    slotLabelFormat={{
                        hour: 'numeric',
                        minute: '2-digit',
                        omitZeroMinute: omitZeroMinute,
                        meridiem: 'short'
                    }}
                    scrollTime='08:00:00'
                    height={'auto'}
                    stickyHeaderDates={'auto'}
                    loading={setLoading}
                    events={eventSource}
                    resources={resourceSource}
                    editable={true}
                    eventResize={handleResize}
                    eventClick={handleClick}
                    eventContent={eventContent}
                    dayHeaderContent={dayHeaderContent}
                    eventDrop={eventDrop}
                    select={handleSelectRegion}
                    resourceLabelContent={resourceLabelContent}
                    filterResourcesWithEvents={hideEmpty}
                    customButtons={{
                        clearResFilter: {
                            text: 'Everyone',
                            click: () => setTeamFilter([])
                        }
                    }}
                    headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'clearResFilter,resourceTimeGridDay,resourceTimeGridWeek'
                    }}

                    schedulerLicenseKey={schedulerLicenseKey}
                />
                <div>
                    <div style={floatStyle} >
                        <label>Rota Mode</label>
                        <input disabled={hasDiaryRotaPerm ? '' : 'disabled'} type='checkbox' name='rotaMode' onChange={handleModeChange} checked={rotaMode} />
                    </div>
                    <div style={floatStyle} >
                        <label>Hide People not Working</label>
                        <input type='checkbox' name='hideEmpty' onChange={handleModeChange} checked={hideEmpty} />
                    </div>
                    <div style={floatStyle}>
                        {false && <><label>Quick Edit Mode</label>
                            <input type='checkbox' name='qeMode' onChange={handleModeChange} checked={qeMode} /></>}
                    </div>
                </div>
            </>
        )
    }
}

export default AltCalendar


